import React, { Fragment } from 'react';
import { LeftMenuUser } from '../LeftMenuUser';
import { CurrentUserLimits } from '../../../../slices/current-user/current-user.state';
import {
    PRACTICE_STATUS_ENUM,
    AssociateMemberForNonprofitStatus,
} from '../../../../utils/constants';
import { getMappedAssosiateMemberRights } from '../../../../utils/associateMembersConstants';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { FeatureFlag } from '../../../../containers/FeatureFlags/FeatureFlag';
import { FeatureFlagsEnum } from '../../../../containers/FeatureFlags/FeatureFlags.enum';
import { HomeMenuItem } from '../items/HomeMenuItem';
import { MyAccountMenuItem } from '../items/MyAccountMenuItem';
import { NotificationSettingsMenuItem } from '../items/NotificationSettingsMenuItem';
import { MyPracticesMenuItem } from '../items/MyPracticesMenuItem';
import { MyNonprofitMenuItem } from '../items/MyNonprofitMenuItem';
import { MarketplaceDashboardMenuItem } from '../items/MarketplaceDashboardMenuItem';
import { MarketplaceTeamMembersMenuItem } from '../items/MarketplaceTeamMembersMenuItem';
import { MyGroupsMenuItem } from '../items/MyGroupsMenuItem';
import { MyReferralHubMenuItem } from '../items/MyReferralHubMenuItem';
import { MyEngagementsMenuItem } from '../items/MyEngagementsMenuItem';
import { MyDealsMenuItem } from '../items/MyDealsMenuItem';
import { ObservingDealsMenuItem } from '../items/ObservingDealsMenuItem';

const HEADING = 'app-sidebar__heading vertical-nav-menu';
const HEADING_CUSTOM = 'app-sidebar__heading vertical-nav-menu custom';

export interface SecondaryUserMenuProps {
    user: LeftMenuUser;
    limits: CurrentUserLimits;
}

export const SecondaryUserMenu = (props: SecondaryUserMenuProps) => {
    const { user, limits } = props;
    const practices = user.practices || [];
    const isAnyPracticeApproved =
        practices.filter((p) => p.status === PRACTICE_STATUS_ENUM.Approved)
            .length > 0;
    const isMarketplaceBuyer = !!user.isMarketplaceBuyer;
    const isMarketplaceBroker = !!user.isMarketplaceBroker;
    const associateMember = user.associateMember || [];
    const nonprofitMapping = associateMember[0]?.nonprofitMapping;
    const engagements = user.engagements || [];

    const isActingPrimaryBuyer =
        isMarketplaceBuyer &&
        associateMember.some((am: any) =>
            am.associateMemberMarketplaceMappings?.some(
                (m: any) => m.isGeneralSettings && m.isActingPrimaryBuyer
            )
        );

    const isAssociateHaveAccessToSeeReferralHub =
        associateMember.length > 0 &&
        (associateMember[0]?.practices?.some(
            (p: any) =>
                getMappedAssosiateMemberRights(p.acceptedAccessRights)
                    .referralPartnerExtension
        ) ||
            (getMappedAssosiateMemberRights(
                nonprofitMapping?.associateMemberAccessRights
            ).referralPartnerExtension &&
                nonprofitMapping?.associateMemberStatus ===
                    AssociateMemberForNonprofitStatus.APPROVED));

    const showNonprofit =
        nonprofitMapping?.associateMemberStatus ===
            AssociateMemberForNonprofitStatus.APPROVED &&
        !nonprofitMapping?.isMemberBelongToFormerNonprofitAdmin;

    const hasSellerEngagement =
        (practices.some((p: any) => p.isInEngagement) ||
            associateMember.some((am: any) =>
                am.associateMemberMarketplaceMappings?.some(
                    (m: any) =>
                        m.isSellerSide &&
                        m.currentSellerTMPermissions?.length > 0
                )
            )) &&
        engagements.some((e: any) => e.party === TimelineParty.Seller);

    return (
        <Fragment>
            <div id="left-menu" />
            <div>
                <HomeMenuItem
                    className={isAnyPracticeApproved ? HEADING : HEADING_CUSTOM}
                />
                <MyAccountMenuItem />
                <FeatureFlag flag={FeatureFlagsEnum.FF_Notifications}>
                    <NotificationSettingsMenuItem />
                </FeatureFlag>

                {showNonprofit ? (
                    <MyNonprofitMenuItem
                        charityId={nonprofitMapping.nonprofitId}
                    />
                ) : (
                    <MyPracticesMenuItem
                        practices={user.practices}
                        showHeading={!isMarketplaceBuyer}
                        headingClassName={HEADING}
                    />
                )}

                {!isMarketplaceBuyer && (
                    <MyGroupsMenuItem
                        summary={user}
                        limits={limits}
                        headingClassName={
                            isAnyPracticeApproved
                                ? 'app-sidebar__heading vertical-nav-menu hidden-background'
                                : 'app-sidebar__heading vertical-nav-menu custom hidden-background'
                        }
                    />
                )}

                {isAssociateHaveAccessToSeeReferralHub && (
                    <MyReferralHubMenuItem
                        className={
                            isAnyPracticeApproved ||
                            isAssociateHaveAccessToSeeReferralHub
                                ? HEADING
                                : HEADING_CUSTOM
                        }
                    />
                )}

                {isMarketplaceBuyer && <MarketplaceDashboardMenuItem />}

                {isActingPrimaryBuyer && <MarketplaceTeamMembersMenuItem />}

                {hasSellerEngagement && (
                    <MyEngagementsMenuItem engagements={engagements} />
                )}
            </div>

            {isMarketplaceBroker &&
                engagements.some((e: any) => e.party === TimelineParty.Broker) && (
                    <MyDealsMenuItem engagements={engagements} />
                )}
            {isMarketplaceBroker &&
                engagements.some(
                    (e: any) => e.party === TimelineParty.Observer
                ) && <ObservingDealsMenuItem engagements={engagements} />}
        </Fragment>
    );
};
